import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "../../util/services";
import { mountEmbeddedCheckout } from "../../util/stripeCheckout";
import { normalizeAppLanguage } from "../../util/locale";
import styles from "./signup.module.scss";

const TERMS_URL = {
  tr: "https://pivony.com/tr/kullanim-kosullari",
  default: "https://pivony.com/terms-of-use"
};

/**
 * Sonic Survey Free card step. The card is saved in Stripe (setup mode) and never
 * charged; it keeps out competitors and casual sign-ups. The API refuses Survey
 * activation with CARD_REQUIRED until this step is done.
 */
export default function SurveyCardStep({ onReady }) {
  const { t, i18n } = useTranslation();
  const box = useRef(null);
  const ready = useRef(onReady);
  ready.current = onReady;
  const returning = new URLSearchParams(window.location.search).get(
    "session_id"
  );
  const [error, setError] = useState("");
  const lang = normalizeAppLanguage(i18n.language);
  const termsUrl = TERMS_URL[lang] || TERMS_URL.default;

  // Back from Stripe: record the saved card, then the caller activates Survey.
  useEffect(() => {
    if (!returning) return;
    apiRequest(
      "subscriptions/survey/self-serve/complete",
      "POST",
      { session_id: returning },
      true
    ).then(result => {
      if (result && result.card_saved && ready.current) ready.current(result);
      else setError((result && result.error) || t("signup_survey_card.error"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returning]);

  useEffect(() => {
    if (returning) return undefined;
    let checkout;
    let gone = false;
    const returnUrl =
      `${window.location.origin}/signup?product=survey&lang=${lang}` +
      "&card=return&session_id={CHECKOUT_SESSION_ID}";
    apiRequest(
      "subscriptions/survey/self-serve/setup",
      "POST",
      { return_url: returnUrl },
      true
    ).then(async result => {
      if (gone || !result) return;
      if (result.error || !result.client_secret) {
        setError(result.error || t("signup_survey_card.error"));
        return;
      }
      if (!box.current) return;
      checkout = await mountEmbeddedCheckout({
        publishableKey: result.publishable_key,
        clientSecret: result.client_secret,
        container: box.current
      });
      if (gone && checkout && checkout.destroy) checkout.destroy();
    });
    return () => {
      gone = true;
      if (checkout && checkout.destroy) checkout.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returning, lang]);

  const retry = () => {
    window.location.replace(`/signup?product=survey&lang=${lang}`);
  };

  return (
    <div className={`${styles.container} ${styles.containerActivation}`}>
      <div className={styles.signUpContainer}>
        <div className={styles.activationPanel}>
          <h1>{t("signup_survey_card.title")}</h1>
          <p>{t("signup_survey_card.body")}</p>
          {error ? (
            <>
              <p className={styles.termsError}>{error}</p>
              <button
                type="button"
                className={styles.cardRetry}
                onClick={retry}
              >
                {t("signup_survey_card.retry")}
              </button>
            </>
          ) : null}
          <div ref={box} className={styles.cardMount} />
          <p className={styles.activationNote}>
            {t("signup_survey_card.terms_prefix")}{" "}
            <a href={termsUrl} target="_blank" rel="noopener noreferrer">
              {t("signup_survey_card.terms_link")}
            </a>
            {" · "}
            <a
              href="https://stripe.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
